import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FAQSection from '../components/FAQSection';
import { faqs } from '../mockData';
import { UserPlus, CheckCircle, Shield, Clock, Gift, ChevronDown, ChevronUp } from 'lucide-react';

const Registrieren = () => {
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  useEffect(() => {
    document.title = 'Robocat Casino Registrieren & Login | Jetzt Konto erstellen';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Robocat Casino Registrierung ⚡ Einfacher Login ✓ Schnelle Anmeldung ✓ 500€ Willkommensbonus ✓ Sichere Verifizierung');
    }
  }, []);

  const registrationSteps = [
    {
      icon: <UserPlus className="w-8 h-8" />,
      title: 'Konto erstellen',
      description: 'Klicken Sie auf "Registrieren" und geben Sie Ihre persönlichen Daten ein: Name, E-Mail, Geburtsdatum und Adresse. Erstellen Sie ein sicheres Passwort mit mindestens 8 Zeichen.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'E-Mail bestätigen',
      description: 'Sie erhalten eine Bestätigungs-E-Mail. Klicken Sie auf den Link, um Ihre E-Mail-Adresse zu verifizieren. Dies dauert nur wenige Sekunden.'
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: 'Erste Einzahlung',
      description: 'Tätigen Sie Ihre erste Einzahlung (mindestens 20 €) und erhalten Sie automatisch Ihren Willkommensbonus von 100% bis zu 500 € plus 200 Freispiele.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Spielen & Gewinnen',
      description: 'Ihr Konto ist bereit! Wählen Sie aus über 3.000 Spielen und starten Sie Ihr Casino-Abenteuer mit dem Bonusguthaben.'
    }
  ];

  const loginFeatures = [
    {
      title: 'Schneller Zugang',
      description: 'Loggen Sie sich in Sekunden ein mit Ihrer E-Mail und Passwort. Kein komplizierter Prozess.'
    },
    {
      title: 'Sicheres Login',
      description: 'SSL-Verschlüsselung und Zwei-Faktor-Authentifizierung schützen Ihr Konto vor unbefugtem Zugriff.'
    },
    {
      title: 'Passwort vergessen?',
      description: 'Kein Problem! Nutzen Sie die "Passwort vergessen"-Funktion für eine schnelle Wiederherstellung.'
    },
    {
      title: 'Mobile Login',
      description: 'Greifen Sie von jedem Gerät auf Ihr Konto zu - Desktop, Tablet oder Smartphone.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
      {/* Header with Banner */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/aigi8kvv_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.14.06.png" 
            alt="Robocat Casino Registrieren Banner" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-blue-950/95"></div>
        </div>
        
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <UserPlus className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Robocat Casino <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Registrieren</span> & Login
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Erstellen Sie in wenigen Minuten Ihr Robocat Casino Konto und sichern Sie sich 500 € Willkommensbonus plus 200 Freispiele. Der Registrierungsprozess ist einfach, schnell und vollständig sicher.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300"
                onClick={() => alert('Demo: Zur Registrierung')}
              >
                Jetzt registrieren
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 px-8 py-6 text-lg transition-all duration-300"
                onClick={() => alert('Demo: Zum Login')}
              >
                Zum Login
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Steps */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">So registrieren Sie sich in 4 einfachen Schritten</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {registrationSteps.map((step, index) => {
                const stepImages = [
                  "https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/86n5ynh1_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.14.00.png",
                  "https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/3royeyz9_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.13.47.png",
                  "https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/rdpp1v0r_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2008.20.10.png",
                  "https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/abge20ij_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2006.09.25.png"
                ];
                
                return (
                  <Card key={index} className="bg-slate-800/50 border-cyan-500/20 p-6 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-15">
                      <img 
                        src={stepImages[index]} 
                        alt={`${step.title} Schritt ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-20">
                      {index + 1}
                    </div>
                    <div className="relative z-10">
                      <div className="text-cyan-400 mb-4 ml-8">{step.icon}</div>
                      <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                      <p className="text-gray-300 leading-relaxed">{step.description}</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Bonus for New Players */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-cyan-500/30 p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/aigi8kvv_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.14.06.png" 
                  alt="Willkommensbonus" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-cyan-500/10 to-transparent rounded-full"></div>
              <div className="relative z-10">
                <Gift className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Exklusiver Willkommensbonus für Neukunden</h2>
                <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">
                  500 €
                </div>
                <div className="text-xl text-cyan-400 font-semibold mb-6">+ 200 Freispiele + 1 Bonus Crab</div>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  Registrieren Sie sich heute und erhalten Sie automatisch unseren großzügigen Willkommensbonus. Verdoppeln Sie Ihre erste Einzahlung bis zu 500 € und genießen Sie 200 Freispiele für die beliebtesten Slots.
                </p>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold px-10 py-6 text-lg shadow-2xl"
                  onClick={() => alert('Demo: Bonus sichern')}
                >
                  Bonus jetzt sichern
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Login Features */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Robocat Casino Login Features</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {loginFeatures.map((feature, index) => (
                <Card key={index} className="bg-slate-800/50 border-cyan-500/20 p-6 hover:border-cyan-500/50 transition-all duration-300">
                  <CheckCircle className="w-8 h-8 text-cyan-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Notice */}
      <section className="py-12 bg-slate-900 border-y border-cyan-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-4">Ihre Sicherheit hat Priorität</h3>
            <p className="text-gray-300 leading-relaxed">
              Robocat Casino verwendet modernste SSL-256-Bit-Verschlüsselung zum Schutz Ihrer persönlichen Daten und Transaktionen. Wir sind vollständig lizenziert und reguliert und befolgen strenge Datenschutzrichtlinien gemäß DSGVO. Ihre Daten werden niemals an Dritte weitergegeben.
            </p>
          </div>
        </div>
      </section>

      {/* SEO Content with Dropdown - Before FAQs */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800/50 border border-cyan-500/20 rounded-2xl overflow-hidden">
              <button
                onClick={() => setIsContentExpanded(!isContentExpanded)}
                className="w-full px-8 py-6 flex items-center justify-between hover:bg-slate-800/70 transition-colors"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white text-left">
                  Robocat Casino Login und Registrierung: Vollständiger Leitfaden
                </h2>
                {isContentExpanded ? (
                  <ChevronUp className="w-6 h-6 text-cyan-400 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-cyan-400 flex-shrink-0 ml-4" />
                )}
              </button>
              
              <div className="px-8 pb-8 space-y-6">
                <p className="text-gray-300 leading-relaxed">
                  Die Robocat Casino Registrierung ist schnell, einfach und vollständig kostenlos. Innerhalb von nur 2-3 Minuten können Sie Ihr Konto erstellen...
                </p>
                
                {isContentExpanded && (
                  <div className="space-y-6 animate-in slide-in-from-top duration-300">
                    <p className="text-gray-300 leading-relaxed">
                      Die Robocat Casino Registrierung ist schnell, einfach und vollständig kostenlos. Innerhalb von nur 2-3 Minuten können Sie Ihr Konto erstellen und mit dem Spielen beginnen. Der Robocat Casino Anmeldeprozess erfordert nur grundlegende Informationen wie Name, E-Mail-Adresse, Geburtsdatum und Wohnadresse. Diese Daten werden benötigt, um die Einhaltung der Glücksspielvorschriften zu gewährleisten und Ihr Konto zu schützen. Alle Informationen werden mit modernster SSL-Verschlüsselung gesichert und niemals an Dritte weitergegeben.
                    </p>
                    
                    <img 
                      src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/67iza2ci_6.png" 
                      alt="Robocat Casino Registrierung" 
                      className="w-full rounded-lg border border-cyan-500/20 shadow-xl"
                    />
                    
                    <p className="text-gray-300 leading-relaxed">
                      Nach der erfolgreichen Robocat Casino Registrierung erhalten Sie eine Bestätigungs-E-Mail. Klicken Sie auf den Link in der E-Mail, um Ihre E-Mail-Adresse zu verifizieren. Dies ist ein wichtiger Sicherheitsschritt, der sicherstellt, dass nur Sie Zugriff auf Ihr Konto haben. Sobald Ihre E-Mail bestätigt ist, können Sie sich mit Ihren Anmeldedaten beim Robocat Casino einloggen. Der Robocat Casino Login erfolgt schnell und sicher - geben Sie einfach Ihre E-Mail-Adresse und Ihr Passwort ein, und Sie haben sofortigen Zugriff auf über 3.000 Spiele.
                    </p>
                    
                    <img 
                      src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/ywbmw97l_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2008.38.57.png" 
                      alt="Robocat Casino Login" 
                      className="w-full rounded-lg border border-cyan-500/20 shadow-xl"
                    />
                    
                    <p className="text-gray-300 leading-relaxed">
                      Für maximale Sicherheit empfehlen wir die Aktivierung der Zwei-Faktor-Authentifizierung (2FA) nach dem Robocat Casino Einloggen. Diese zusätzliche Sicherheitsebene schützt Ihr Konto vor unbefugtem Zugriff, selbst wenn jemand Ihr Passwort kennt. Die 2FA-Einrichtung ist einfach und dauert nur wenige Minuten. Sie können wählen zwischen SMS-Codes, Authentifizierungs-Apps wie Google Authenticator oder E-Mail-Bestätigung. Diese Maßnahme gibt Ihnen die Gewissheit, dass Ihr Konto und Ihre Gewinne jederzeit geschützt sind.
                    </p>
                    
                    <p className="text-gray-300 leading-relaxed">
                      Bevor Sie Ihre erste Auszahlung vornehmen können, müssen Sie Ihr Konto vollständig verifizieren. Dieser Prozess ist gesetzlich vorgeschrieben und dient der Geldwäschebekämpfung sowie dem Spielerschutz. Für die Verifizierung benötigen Sie einen gültigen Lichtbildausweis (Personalausweis oder Reisepass) und einen Adressnachweis (Strom-, Gas- oder Wasserrechnung bzw. Kontoauszug), der nicht älter als 3 Monate ist. Laden Sie diese Dokumente einfach über den sicheren Upload-Bereich in Ihrem Konto hoch. Die Überprüfung erfolgt in der Regel innerhalb von 24 Stunden. Nach erfolgreicher Verifizierung steht Ihrem Robocat Casino Erlebnis nichts mehr im Weg!
                    </p>
                  </div>
                )}
                
                <button
                  onClick={() => setIsContentExpanded(!isContentExpanded)}
                  className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors flex items-center space-x-2"
                >
                  <span>{isContentExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}</span>
                  {isContentExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection faqs={faqs.registrieren} title="Häufige Fragen zur Registrierung & Login" />
    </div>
  );
};

export default Registrieren;
